import { FC } from 'react';
import { OrderPaymentStatus } from 'src/models/OrderPayment.interface';
import { PaymentMode } from 'src/models/enums/PaymentMode.enum';
import Status from 'src/components/Status';
import ExpandableText from 'src/components/ExpandableText/ExpandableText';
import { DATE_FORMATS } from 'src/utils/properties.utils';
import { formatCurrency } from 'src/utils/format.utils';
import {
  getPaymentModeText,
  getOrderPaymentStatusText,
  getChequeStatusText,
} from 'src/utils/status.utils';
import {
  ViewStyledDialog,
  FixedHeaderWrapper,
  ContentWrapper,
  HeaderSection,
  HeaderLeft,
  TitleTypography,
  ViewCloseIcon,
  MainContentGrid,
  PaymentDetailsBox,
  PaymentInfoRow,
  InfoBlock,
  InfoBlockRight,
  PaymentMethodCard,
  PaymentMethodRow,
  PaymentMethodDivider,
  StatusInfoBox,
  StatusInfoRow,
  RejectionNoteBox,
  RejectionLabelTypography,
  LabelTypography,
  ValueTypography,
  SmallValueTypography,
  DetailLabelText,
  DetailValueText,
  NoteText,
} from './OrderPaymentDialog.styles';
import { ViewOrderPaymentDialogProps } from 'src/models/OrderPayment.interface';

const ViewOrderPaymentDialog: FC<ViewOrderPaymentDialogProps> = ({
  open,
  onClose,
  payment,
}) => {
  if (!payment) return null;

  const paymentModeValue =
    typeof payment.paymentMode === 'string'
      ? payment.paymentMode
      : payment.paymentMode;
  const isCheque =
    paymentModeValue === PaymentMode.CHEQUE || paymentModeValue === 'Cheque';
  const isOnline =
    paymentModeValue === PaymentMode.ONLINE || paymentModeValue === 'Online';
  const isRejected =
    payment.status === OrderPaymentStatus.REJECTED ||
    payment.status === 'Rejected';
  const isApproved =
    payment.status === OrderPaymentStatus.APPROVED ||
    payment.status === 'Approved';

  return (
    <ViewStyledDialog open={open} onClose={onClose}>
      <FixedHeaderWrapper>
        <HeaderSection>
          <HeaderLeft>
            <TitleTypography>Order Payment Details</TitleTypography>
            <Status status={getOrderPaymentStatusText(payment.status)} />
          </HeaderLeft>
          <ViewCloseIcon
            component="i"
            className="bx bx-x-circle"
            onClick={onClose}
          />
        </HeaderSection>
      </FixedHeaderWrapper>

      <ContentWrapper>
        <MainContentGrid>
          {/* Left Section - Payment Details */}
          <PaymentDetailsBox>
            {/* Date and Amount Row */}
            <PaymentInfoRow>
              <InfoBlock>
                <LabelTypography>Date</LabelTypography>
                <ValueTypography>
                  {new Date(payment.paymentDate).toLocaleDateString(
                    DATE_FORMATS.LOCALE
                  )}
                </ValueTypography>
              </InfoBlock>
              <InfoBlockRight>
                <LabelTypography>Amount (LKR)</LabelTypography>
                <ValueTypography>
                  {formatCurrency(payment.paidAmount)}
                </ValueTypography>
              </InfoBlockRight>
            </PaymentInfoRow>

            {/* Note Row */}
            <PaymentInfoRow>
              <InfoBlock>
                <LabelTypography>Notes</LabelTypography>
                <ValueTypography>
                  <ExpandableText
                    text={payment.notes}
                    lines={2}
                    placeholder="N/A"
                  />
                </ValueTypography>
              </InfoBlock>
            </PaymentInfoRow>

            {/* Payment Mode Row */}
            <PaymentInfoRow>
              <InfoBlock>
                <LabelTypography>Payment Mode</LabelTypography>
                <ValueTypography>
                  {typeof payment.paymentMode === 'string'
                    ? payment.paymentMode
                    : getPaymentModeText(payment.paymentMode)}
                </ValueTypography>
              </InfoBlock>
            </PaymentInfoRow>

            {/* Cheque Details Card */}
            {isCheque && (
              <PaymentMethodCard>
                <PaymentMethodRow>
                  <DetailLabelText>Cheque Date</DetailLabelText>
                  <DetailValueText>
                    {payment.chequeDate
                      ? new Date(payment.chequeDate).toLocaleDateString(
                          DATE_FORMATS.LOCALE
                        )
                      : '-'}
                  </DetailValueText>
                </PaymentMethodRow>
                <PaymentMethodDivider />
                <PaymentMethodRow>
                  <DetailLabelText>Cheque Number</DetailLabelText>
                  <DetailValueText>
                    {payment.chequeNumber || '-'}
                  </DetailValueText>
                </PaymentMethodRow>
                <PaymentMethodDivider />
                <PaymentMethodRow>
                  <DetailLabelText>Cheque Status</DetailLabelText>
                  <DetailValueText>
                    {getChequeStatusText(payment.chequeStatus)}
                  </DetailValueText>
                </PaymentMethodRow>
                {payment.chequeRealizedDate && (
                  <>
                    <PaymentMethodDivider />
                    <PaymentMethodRow>
                      <DetailLabelText>Cheque Realized Date</DetailLabelText>
                      <DetailValueText>
                        {new Date(
                          payment.chequeRealizedDate
                        ).toLocaleDateString(DATE_FORMATS.LOCALE)}
                      </DetailValueText>
                    </PaymentMethodRow>
                  </>
                )}
                <PaymentMethodDivider />
                <PaymentMethodRow>
                  <DetailLabelText>Bank Account</DetailLabelText>
                  <DetailValueText>
                    {`${payment.bankName} - ${payment.accountNumber}` || '-'}
                  </DetailValueText>
                </PaymentMethodRow>
              </PaymentMethodCard>
            )}

            {/* Online Payment Details Card */}
            {isOnline && (
              <PaymentMethodCard>
                <PaymentMethodRow>
                  <DetailLabelText>Reference Number</DetailLabelText>
                  <DetailValueText>
                    {payment.transactionReference || '-'}
                  </DetailValueText>
                </PaymentMethodRow>
                <PaymentMethodDivider />
                <PaymentMethodRow>
                  <DetailLabelText>Bank Account</DetailLabelText>
                  <DetailValueText>
                    {`${payment.bankName} - ${payment.accountNumber}` || '-'}
                  </DetailValueText>
                </PaymentMethodRow>
              </PaymentMethodCard>
            )}
          </PaymentDetailsBox>

          {/* Right Section - Status Info */}
          {isRejected && (
            <StatusInfoBox>
              <StatusInfoRow>
                <InfoBlock>
                  <LabelTypography>Rejected Date</LabelTypography>
                  <SmallValueTypography>
                    {payment.approvedDate
                      ? new Date(payment.approvedDate).toLocaleDateString(
                          DATE_FORMATS.LOCALE
                        )
                      : '-'}
                  </SmallValueTypography>
                </InfoBlock>
                <InfoBlockRight>
                  <LabelTypography>Rejected User</LabelTypography>
                  <SmallValueTypography>
                    {payment.approvedBy || '-'}
                  </SmallValueTypography>
                </InfoBlockRight>
              </StatusInfoRow>
              <RejectionNoteBox>
                <RejectionLabelTypography>
                  Rejection Note
                </RejectionLabelTypography>
                <NoteText>{payment.rejectionNotes || '-'}</NoteText>
              </RejectionNoteBox>
            </StatusInfoBox>
          )}

          {isApproved && (
            <StatusInfoBox>
              <StatusInfoRow>
                <InfoBlock>
                  <LabelTypography>Approved Date</LabelTypography>
                  <SmallValueTypography>
                    {payment.approvedDate
                      ? new Date(payment.approvedDate).toLocaleDateString(
                          DATE_FORMATS.LOCALE
                        )
                      : '-'}
                  </SmallValueTypography>
                </InfoBlock>
                <InfoBlockRight>
                  <LabelTypography>Approved By</LabelTypography>
                  <SmallValueTypography>
                    {payment.approvedBy || '-'}
                  </SmallValueTypography>
                </InfoBlockRight>
              </StatusInfoRow>
            </StatusInfoBox>
          )}
        </MainContentGrid>
      </ContentWrapper>
    </ViewStyledDialog>
  );
};

export default ViewOrderPaymentDialog;
